import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatOption, MatSelect } from '@angular/material/select';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, startWith, take } from 'rxjs';
import { UiButtonComponent, UiColorSelectComponent } from '@ui';
import { UiVehicleIconComponent } from '../../../../../../shared/ui-kit/ui-vehicle-icon/ui-vehicle-icon.component';
import { VehicleModels } from '@models/vehicle.model';
import { VehicleGroupsActions } from '@states/vehicle-groups/vehicle-groups.actions';
import { vehicleGroupsFeature } from '@states/vehicle-groups/vehicle-groups.feature';
import { carMadeModel } from '@consts/carMadeModel';

type SaveVehicleForm = FormGroup<{
  owner: FormControl<string>;
  plate: FormControl<string>;
  make: FormControl<string>;
  color: FormControl<string[]>;
}>;

@Component({
  selector: 'app-save-vehicles-dialog',
  standalone: true,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    UiButtonComponent,
    UiVehicleIconComponent,
    UiColorSelectComponent,
    MatSelect,
    MatOption,
  ],
  templateUrl: './save-vehicles-dialog.component.html',
  styleUrl: './save-vehicles-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveVehiclesDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<SaveVehiclesDialogComponent>);
  private readonly data = inject<VehicleModels.SaveVehiclesDialogData>(MAT_DIALOG_DATA);
  private readonly store$ = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly vehicles = this.data.vehicles;
  protected readonly carMakes = Object.keys(carMadeModel);
  protected readonly vehicleForms = new FormArray<SaveVehicleForm>(
    this.vehicles.map((vehicle) => this.createVehicleForm(vehicle)),
  );
  protected readonly isAddingVehiclesToGroups = this.store$.selectSignal(
    vehicleGroupsFeature.selectIsAddingVehiclesToGroups,
  );
  protected readonly areVehiclesValid = toSignal(
    this.vehicleForms.statusChanges.pipe(
      startWith(this.vehicleForms.status),
      map(() => this.vehicleForms.controls.every((form) => !!form.controls.plate.getRawValue().trim())),
    ),
    { initialValue: this.vehicleForms.valid },
  );

  protected closeDialog(): void {
    if (this.isAddingVehiclesToGroups()) {
      return;
    }
    this.dialogRef.close(false);
  }

  protected saveVehicles(): void {
    if (!this.areVehiclesValid() || this.isAddingVehiclesToGroups()) {
      return;
    }

    const groupIds = this.data.groupIds;
    const unsavedVehicleDtos = this.vehicles.map((vehicle, index) => this.toUnsavedVehicleDto(vehicle, index));

    this.actions$
      .pipe(
        ofType(VehicleGroupsActions.addVehiclesToGroupsSuccess),
        filter((action) => this.hasSameGroupIds(action.groupIds, groupIds)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.dialogRef.close(true));

    this.store$.dispatch(
      VehicleGroupsActions.startAddingVehiclesToGroups({
        groupIds,
        savedVehicleIds: this.data.savedVehicleIds,
        unsavedVehicleDtos,
      }),
    );
  }

  private createVehicleForm(vehicle: VehicleModels.Vehicle): SaveVehicleForm {
    return new FormGroup({
      owner: new FormControl<string>(vehicle.owner ?? '', { nonNullable: true }),
      plate: new FormControl<string>(vehicle.plate?.toUpperCase() ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      make: new FormControl<string>(vehicle.make ?? '', { nonNullable: true }),
      color: new FormControl<string[]>(vehicle.color ? [vehicle.color] : [], { nonNullable: true }),
    });
  }

  private toUnsavedVehicleDto(vehicle: VehicleModels.Vehicle, index: number): VehicleModels.UnsavedVehicleDto {
    const value = this.vehicleForms.controls[index].getRawValue();

    return {
      id: vehicle.id,
      sourcePlate: vehicle.plate,
      plate: value.plate.trim().toLowerCase(),
      owner: value.owner.trim(),
      make: value.make.trim() || undefined,
      color: value.color[0],
      bestImage: vehicle.bestImage,
      cameraId: vehicle.cameraId,
      edgeId: vehicle.edgeId,
      lastSeen: vehicle.lastSeen,
      count: vehicle.count,
    };
  }

  private hasSameGroupIds(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const sortedLeft = [...left].sort();
    const sortedRight = [...right].sort();

    return sortedLeft.every((id, index) => id === sortedRight[index]);
  }
}
